import { useState } from 'react';
import { 
  Mail, 
  MapPin, 
  Phone,
  Send,
  MessageSquare,
  Building,
  User,
  CheckCircle
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';

const ContactPage = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    type: 'general',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: '289b6ad6-f7e6-44c4-9856-f53dd290348c',
          ...formData,
        }),
      });

      const result = await response.json();
      setIsSubmitting(false);

      if (result.success) {
        setSubmitted(true);
        toast.success('Message sent successfully!', {
          description: "We'll get back to you within 24 hours.",
        });
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: '',
          message: '',
          type: 'general',
        });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      setIsSubmitting(false);
      toast.error('Failed to send message. Please try again.');
    }
  };

  const inquiryTypes = [
    { value: 'General-Inquiry', label: 'General Inquiry' },
    { value: 'Training-Programs', label: 'Training Programs' },
    { value: 'Corporate-Training', label: 'Corporate Training' },
    { value: 'Partnership', label: 'Partnership' },
    { value: 'Careers', label: 'Careers' },
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'info@byt3con.training',
      href: 'mailto:info@byt3con.training',
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+91 90040 17799',
      href: 'tel:+919004017799',
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'Mumbai, IN',
      href: '#',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <Toaster 
        position="top-right" 
        theme={theme}
        toastOptions={{
          style: {
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            color: 'var(--text-main)',
          },
        }}
      />
      
      {/* Hero Section */}
      <section className="py-12 md:py-20 relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-0 left-1/3" />
        
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
              ./contact_us
            </p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-[var(--text-main)] mb-6">
              Let's <span className="text-[var(--primary)]">Connect</span>
            </h1>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">
              Have questions about our programs? Want to discuss corporate training? 
              We're here to help you start your cybersecurity journey.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16" data-testid="contact-form-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="font-mono text-xl font-bold text-[var(--text-main)] mb-6">
                  Get In Touch
                </h2>
                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <a
                      key={index}
                      href={info.href}
                      className="flex items-start gap-4 group"
                      data-testid={`contact-info-${info.label.toLowerCase()}`}
                    >
                      <div className="w-12 h-12 bg-[var(--surface)] border border-[var(--border)] flex items-center justify-center group-hover:border-[var(--primary)] transition-colors duration-200">
                        <info.icon className="w-5 h-5 text-[var(--primary)]" />
                      </div>
                      <div>
                        <p className="text-[var(--text-muted)] text-sm uppercase tracking-wider">
                          {info.label}
                        </p>
                        <p className="text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors duration-200">
                          {info.value}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Office Hours
              <div className="bg-[var(--surface)] border border-[var(--border)] p-6">
                <h3 className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
                  Office Hours
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Monday - Friday</span>
                    <span className="text-[var(--text-main)] font-mono">9:00 - 18:00 IST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Saturday</span>
                    <span className="text-[var(--text-main)] font-mono">10:00 - 14:00 IST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Sunday</span>
                    <span className="text-[var(--text-main)] font-mono">Closed</span>
                  </div>
                </div>
              </div> */}

              {/* Response Time */}
              <div className="bg-[var(--surface)] border border-[var(--border)] p-6">
                <h3 className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
                  Response Time
                </h3>
                <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                  We typically respond within <span className="text-[var(--primary)] font-mono">24 hours</span> during business days. 
                  For urgent matters, please call us directly.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[var(--surface)] border border-[var(--border)] p-8 md:p-12">
                {/* Terminal Header */}
                <div className="flex items-center gap-2 mb-8 pb-6 border-b border-[var(--border)]">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                  <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                  <div className="w-3 h-3 rounded-full bg-[#27ca40]" />
                  <span className="ml-4 font-mono text-sm text-[var(--text-muted)]">
                    send_message.sh
                  </span>
                </div>

                {submitted ? (
                  <div className="text-center py-12" data-testid="form-success">
                    <div className="w-20 h-20 bg-[var(--primary)]/10 border border-[var(--primary)] flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-[var(--primary)]" />
                    </div>
                    <h3 className="font-mono text-2xl font-bold text-[var(--text-main)] mb-4">
                      Message Sent!
                    </h3>
                    <p className="text-[var(--text-muted)] mb-8">
                      Thanks for reaching out. We'll get back to you shortly.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="font-mono text-[var(--primary)] hover:underline"
                      data-testid="send-another-btn"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {/* Honeypot field for anti-spam */}
                    <input type="checkbox" name="botcheck" className="hidden" />

                    {/* Inquiry Type */}
                    <div>
                      <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-3">
                        <span className="text-[var(--text-muted)]">$</span> inquiry_type
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {inquiryTypes.map((type) => (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, type: type.value }))}
                            className={`px-4 py-2 font-mono text-sm border transition-colors duration-200 ${
                              formData.type === type.value
                                ? 'border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]'
                                : 'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--primary)]/50'
                            }`}
                            data-testid={`inquiry-type-${type.value}`}
                          >
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Name & Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-3">
                          <span className="text-[var(--text-muted)]">$</span> name
                        </label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Your name"
                            className="w-full bg-[var(--background)] border border-[var(--border)] text-[var(--primary)] font-mono p-4 pl-12 focus:border-[var(--primary)] focus:outline-none placeholder:text-[var(--primary)]/30"
                            data-testid="input-name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-3">
                          <span className="text-[var(--text-muted)]">$</span> email
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="john@example.com"
                            className="w-full bg-[var(--background)] border border-[var(--border)] text-[var(--primary)] font-mono p-4 pl-12 focus:border-[var(--primary)] focus:outline-none placeholder:text-[var(--primary)]/30"
                            data-testid="input-email"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Company (optional) */}
                    <div>
                      <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-3">
                        <span className="text-[var(--text-muted)]">$</span> company <span className="text-[var(--text-muted)] text-xs"></span>
                      </label>
                      <div className="relative">
                        <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Acme Inc."
                          className="w-full bg-[var(--background)] border border-[var(--border)] text-[var(--primary)] font-mono p-4 pl-12 focus:border-[var(--primary)] focus:outline-none placeholder:text-[var(--primary)]/30"
                          data-testid="input-company"
                        />
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-3">
                        <span className="text-[var(--text-muted)]">$</span> subject
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="How can we help?"
                        className="w-full bg-[var(--background)] border border-[var(--border)] text-[var(--primary)] font-mono p-4 focus:border-[var(--primary)] focus:outline-none placeholder:text-[var(--primary)]/30"
                        data-testid="input-subject"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-3">
                        <span className="text-[var(--text-muted)]">$</span> message
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-[var(--text-muted)]" />
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={6}
                          placeholder="Tell us more about your needs..."
                          className="w-full bg-[var(--background)] border border-[var(--border)] text-[var(--primary)] font-mono p-4 pl-12 focus:border-[var(--primary)] focus:outline-none placeholder:text-[var(--primary)]/30 resize-none"
                          data-testid="input-message"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[var(--primary)] text-black font-mono font-bold px-8 py-4 uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)] hover:shadow-[0_0_25px_var(--glow-primary)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>

                    {/* Terminal prompt */}
                    <div className="pt-4 border-t border-[var(--border)]">
                      <code className="font-mono text-xs text-[var(--text-muted)]">
                        <span className="text-[var(--primary)]">$</span> ./submit --confirm
                        <span className="cursor-blink ml-1 inline-block w-2 h-3 bg-[var(--primary)]" />
                      </code>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map/Location Section */}
      <section className="py-12 md:py-20  border-t border-[var(--border)]" data-testid="location-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
              ./headquarters
            </p>
            {/* <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[var(--text-main)]">
              Visit Us
            </h2> */}
          </div>
          
          {/* Placeholder for map */}
          <div className="bg-[var(--surface-highlight)] border border-[var(--border)] h-80 flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-50" />
            <div className="relative z-10 text-center">
              <MapPin className="w-12 h-12 text-[var(--primary)] mx-auto mb-4" />
              <p className="font-mono text-[var(--text-main)] text-lg">Mumbai, Maharashtra</p>
              <p className="text-[var(--text-muted)] text-sm mt-2">India</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
